/**
 * Mesh Identity Module
 *
 * Generates and persists the local device's cryptographic identity for the
 * Bitchat-compatible mesh layer. Each device has:
 *   - A Curve25519 key pair (for future Noise XX handshakes and HTTP Signatures)
 *   - An 8-byte peer ID derived from SHA-256(publicKey), used as the compact
 *     sender/recipient identifier in MeshPacket headers
 *   - A user-chosen nickname (self-asserted, verified via ActivityPub actor
 *     linkage in a later slice)
 *
 * Storage: IndexedDB (object store `identity`), survives reloads. The key pair
 * is persisted as JWK so it can be reimported on next launch.
 *
 * NOTE: Web Crypto does not expose Curve25519 uniformly yet (Chrome/Firefox
 * added Ed25519/X25519 in 2023 behind subtle.generateKey with name 'Ed25519'
 * or 'X25519'). We generate an X25519 pair for future ECDH handshakes and an
 * Ed25519 pair for signing. If the runtime lacks support, we fall back to
 * an ECDSA P-256 pair and mark capability `legacyKeys: true` so the protocol
 * layer can downgrade gracefully.
 */

const DB_NAME = 'bisawofa-mesh';
const DB_VERSION = 1;
const STORE = 'identity';
const LOCAL_KEY = 'local';

let cached = null;

// ---------- IndexedDB helpers ----------

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ---------- key generation ----------

async function generateKeys() {
  const subtle = crypto.subtle;
  let x25519, ed25519, legacy = false;

  try {
    x25519 = await subtle.generateKey({ name: 'X25519' }, true, ['deriveBits']);
    ed25519 = await subtle.generateKey({ name: 'Ed25519' }, true, ['sign', 'verify']);
  } catch (_) {
    legacy = true;
    x25519 = await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
    ed25519 = await subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, true, ['sign', 'verify']);
  }

  const x_pub = await subtle.exportKey('jwk', x25519.publicKey);
  const x_prv = await subtle.exportKey('jwk', x25519.privateKey);
  const e_pub = await subtle.exportKey('jwk', ed25519.publicKey);
  const e_prv = await subtle.exportKey('jwk', ed25519.privateKey);

  return { x_pub, x_prv, e_pub, e_prv, legacy };
}

async function importKeys(stored) {
  const subtle = crypto.subtle;
  const { x_pub, x_prv, e_pub, e_prv, legacy } = stored;

  const xAlg = legacy ? { name: 'ECDH', namedCurve: 'P-256' } : { name: 'X25519' };
  const eAlg = legacy ? { name: 'ECDSA', namedCurve: 'P-256' } : { name: 'Ed25519' };

  const x25519 = {
    publicKey:  await subtle.importKey('jwk', x_pub, xAlg, true, []),
    privateKey: await subtle.importKey('jwk', x_prv, xAlg, true, ['deriveBits']),
  };
  const ed25519 = {
    publicKey:  await subtle.importKey('jwk', e_pub, eAlg, true, ['verify']),
    privateKey: await subtle.importKey('jwk', e_prv, eAlg, true, ['sign']),
  };

  return { x25519, ed25519, legacy };
}

// ---------- peer ID derivation ----------

async function derivePeerId(publicKeyJwk) {
  // SHA-256 over the raw X-coordinate (jwk.x is base64url-encoded)
  const raw = b64urlDecode(publicKeyJwk.x);
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', raw));
  return hash.slice(0, 8); // 8-byte peer ID (Bitchat convention)
}

function b64urlDecode(s) {
  const pad = '='.repeat((4 - s.length % 4) % 4);
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ---------- public API ----------

/**
 * Load or create the local mesh identity.
 * Idempotent — returns the cached identity on subsequent calls.
 *
 * @param {{
 *   nickname?: string,
 *   fresh?: boolean,  // if true, skip IndexedDB and generate a per-tab
 *                     // identity (used during dev-mode smoke tests where
 *                     // two tabs of the same origin must act as distinct
 *                     // peers; real BLE/Capacitor transports flip this
 *                     // back to false so identity is device-stable).
 * }} [opts]
 * @returns {Promise<{
 *   peerId: Uint8Array,
 *   peerIdHex: string,
 *   nickname: string,
 *   x25519: CryptoKeyPair,
 *   ed25519: CryptoKeyPair,
 *   legacy: boolean,
 *   publicKeyJwk: JsonWebKey,
 *   ephemeral: boolean,
 * }>}
 */
export async function loadIdentity(opts = {}) {
  if (cached) return cached;

  const fresh = opts.fresh === true;
  let stored = fresh ? null : await idbGet(LOCAL_KEY);
  if (!stored) {
    const generated = await generateKeys();
    stored = {
      ...generated,
      nickname: opts.nickname || generateRandomNickname(),
      createdAt: Date.now(),
    };
    if (!fresh) {
      await idbPut(LOCAL_KEY, stored);
      console.log('[Identity] Generated new device identity (persistent)');
    } else {
      console.log('[Identity] Generated ephemeral per-tab identity (dev mode)');
    }
  }

  const { x25519, ed25519, legacy } = await importKeys(stored);
  const peerId = await derivePeerId(stored.x_pub);

  cached = {
    peerId,
    peerIdHex: Array.from(peerId, b => b.toString(16).padStart(2, '0')).join(''),
    nickname: stored.nickname,
    x25519,
    ed25519,
    legacy,
    publicKeyJwk: stored.x_pub,
    ephemeral: fresh,
    // Raw 32-byte public keys for the TLV announce payload. For the legacy
    // P-256 fallback these are longer than 32 bytes, so consumers should
    // check length before use.
    noisePublicKey:   b64urlDecode(stored.x_pub.x),
    signingPublicKey: b64urlDecode(stored.e_pub.x),
  };

  return cached;
}

/**
 * Update the local nickname and persist it.
 */
export async function setNickname(nickname) {
  const stored = await idbGet(LOCAL_KEY);
  if (!stored) throw new Error('Identity not yet created');
  stored.nickname = nickname;
  await idbPut(LOCAL_KEY, stored);
  if (cached) cached.nickname = nickname;
}

/**
 * Erase the stored identity. Used by the future "wipe" action.
 */
export async function wipeIdentity() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(LOCAL_KEY);
    tx.oncomplete = () => { cached = null; resolve(); };
    tx.onerror = () => reject(tx.error);
  });
}

function generateRandomNickname() {
  const adjectives = ['Swift', 'Quiet', 'Bright', 'Kind', 'Bold', 'Calm', 'Sharp', 'Warm'];
  const animals   = ['Falcon', 'Antelope', 'Elephant', 'Leopard', 'Hornbill', 'Zebra', 'Gecko', 'Crane'];
  const a = adjectives[Math.floor(Math.random() * adjectives.length)];
  const b = animals[Math.floor(Math.random() * animals.length)];
  const n = Math.floor(Math.random() * 90 + 10);
  return `${a}${b}${n}`;
}

export default { loadIdentity, setNickname, wipeIdentity };
